// Onboarding page url config
export const onboardingPageMap = {
  genderPage: '/onboarding/gender',
  namePage: '/onboarding/name',
  agePage: '/onboarding/age',
  locationPage: '/onboarding/location',
  concernPage: '/onboarding/concern',
} as const;

declare global {
  export type OnboardingPage = keyof typeof onboardingPageMap;
  export type OnboardingPageUrl = typeof onboardingPageMap[OnboardingPage];
}

/** Resolved by `OnboardingService`, so this class stays free of DI. */
export interface OnboardingProfileBreakdownConfig {
  enabled: boolean;
  /** Under `skip_age_page`, Age is never asked, so it must not gate onboarding. */
  skipAgePage: boolean;
}

const PROFILE_BREAKDOWN_DISABLED: OnboardingProfileBreakdownConfig = { enabled: false, skipAgePage: false };

// Onboarding helper class to check onboarding status and state of the user
// !important: Don't import it directly in components or modules.
// TODO(rajesh@heallo) Limit import at compile time
export class Onboarding {
  constructor(
    private user: Record<string, any>,
    private isUserWithoutMainConcern: boolean,
    private profileBreakdown: OnboardingProfileBreakdownConfig = PROFILE_BREAKDOWN_DISABLED,
  ) { }

  hasGenderData(): boolean {
    if (!this.profileBreakdown.enabled) return this.user.Gender && this.user.Age;
    return !this.pendingProfilePage();
  }

  /**
   * First unanswered page of the split profile flow, or null when the profile step is complete.
   * Age is skipped under `skip_age_page` — requiring it there would trap the user in a redirect loop.
   */
  pendingProfilePage(): OnboardingPageUrl | null {
    if (!this.profileBreakdown.enabled) return null;
    if (!this.user.Gender) return onboardingPageMap.genderPage;
    if (!this.user.PatientName) return onboardingPageMap.namePage;
    if (!this.profileBreakdown.skipAgePage && !this.user.Age) return onboardingPageMap.agePage;
    return null;
  }

  hasLocationData(): boolean {
    if (this.profileBreakdown.enabled) return true;
    return this.user.lastLocation;
  }

  hasConcernData(): boolean {
    return !this.isUserWithoutMainConcern;
  }

  validateOnboarding(): OnboardingPageUrl | null {
    if (!this.hasGenderData()) return this.pendingProfilePage() ?? onboardingPageMap.genderPage;
    if (!this.hasLocationData()) return onboardingPageMap.locationPage;
    if (!this.hasConcernData()) return onboardingPageMap.concernPage;
    return null;
  }
}
